import { z } from "zod";

import { unlistedEnumValueSchema } from "../../shared/core/enum-read.schema";
import {
  VIBE_AUDIT_PAYLOAD_BRANCHES_READ_AS_EMITTED,
  VIBE_AUDIT_PAYLOAD_READ_COUNTERPARTS
} from "./payload-branches";

/**
 * Every audit event type this build of the contract has a branch for, as the
 * readers parse it — each enum field lenient. A payload whose event type is
 * listed and whose fields do not fit its branch still fails: that is a broken
 * row, not a newer backend.
 */
export const VibeAuditEventListedReadPayloadSchema = z.discriminatedUnion("eventType", [
  ...VIBE_AUDIT_PAYLOAD_BRANCHES_READ_AS_EMITTED,
  ...VIBE_AUDIT_PAYLOAD_READ_COUNTERPARTS
]);
export type VibeAuditEventListedReadPayload = z.infer<typeof VibeAuditEventListedReadPayloadSchema>;

/** The event types {@link VibeAuditEventListedReadPayloadSchema} has a branch for. */
export const VIBE_AUDIT_READ_EVENT_TYPES: readonly string[] = [
  ...VibeAuditEventListedReadPayloadSchema.options.map((branch) => branch.shape.eventType.value)
];

/**
 * An audit event whose type this contract has no branch for: a newer backend
 * learned it first. It parses instead of failing the feed page, keeps the
 * server's own event type — branded, so it cannot be mistaken for a listed one
 * — and keeps every field the server wrote, so a reader can render it as a
 * neutral row in the server's words rather than dropping it.
 *
 * It sits OUTSIDE the discriminated union on purpose: a member whose
 * discriminator is not a literal disables literal narrowing for every member.
 * Readers ask {@link isListedVibeAuditPayload} first and switch on the listed
 * half; the compiler refuses a switch that skips the question.
 */
export const VibeAuditUnlistedEventReadPayloadSchema = z.looseObject({
  eventType: unlistedEnumValueSchema(VIBE_AUDIT_READ_EVENT_TYPES)
});
export type VibeAuditUnlistedEventReadPayload = z.infer<
  typeof VibeAuditUnlistedEventReadPayloadSchema
>;

/**
 * An audit row whose stored payload does not fit the branch of its OWN event
 * type — a legacy row predating a required field, or one written by a bug. The
 * backend sends it in this shape instead of dropping it, so the feed still shows
 * that the event happened.
 *
 * `eventType` is the row's indexed column, as the server stored it. `raw` is the
 * stored payload exactly as read, and `issuePaths` names where validation failed,
 * each path joined with `.`, so an operator can see what is wrong without a
 * database session.
 *
 * Told apart from every other arm by the required `malformedPayload` key, which
 * no listed branch carries: its event type may well be a listed one, so the
 * type alone cannot say which arm it is. Like the unlisted arm, it sits outside
 * the discriminated union, and readers ask {@link isMalformedVibeAuditPayload}.
 */
export const VibeAuditMalformedEventReadPayloadSchema = z.object({
  eventType: z.string().min(1),
  malformedPayload: z.object({
    raw: z.unknown(),
    issuePaths: z.array(z.string())
  })
});
export type VibeAuditMalformedEventReadPayload = z.infer<
  typeof VibeAuditMalformedEventReadPayloadSchema
>;

/**
 * The audit payload as the feed's READERS see it: what the console's generated
 * hook parses, and what the CLI's hand-declared wire types are held to by their
 * conformance gate. The backend never writes through this one.
 *
 * For anything the backend sends, the order of the arms decides nothing: no
 * listed branch accepts the malformed shape, and the malformed arm requires its
 * marker. Listed comes first so that a payload which genuinely fits its branch is
 * always read as listed, with its fields, and never as malformed.
 */
export const VibeAuditEventReadPayloadSchema = z.union([
  VibeAuditEventListedReadPayloadSchema,
  VibeAuditMalformedEventReadPayloadSchema,
  VibeAuditUnlistedEventReadPayloadSchema
]);
export type VibeAuditEventReadPayload = z.infer<typeof VibeAuditEventReadPayloadSchema>;

/** True for a row whose stored payload did not fit its own event type's branch. */
export function isMalformedVibeAuditPayload(
  payload: VibeAuditEventReadPayload
): payload is VibeAuditMalformedEventReadPayload {
  return "malformedPayload" in payload;
}

/**
 * True for a payload this contract has a branch for AND that fits it. Every
 * reader asks this before it switches on `eventType`; the listed half then
 * narrows exhaustively.
 *
 * Testing the event type alone is not enough: a malformed row carries a LISTED
 * event type with none of that branch's fields, so it would reach every `case`
 * with nothing in it.
 */
export function isListedVibeAuditPayload(
  payload: VibeAuditEventReadPayload
): payload is VibeAuditEventListedReadPayload {
  return (
    !isMalformedVibeAuditPayload(payload) && VIBE_AUDIT_READ_EVENT_TYPES.includes(payload.eventType)
  );
}
